import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Key, Plus, Loader2, Copy, Check, Trash2, Shield, AlertTriangle, ArrowLeft, Sparkles,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form state
  const [name, setName] = useState('');
  const [mode, setMode] = useState('generate'); // 'generate' | 'custom'
  const [customValue, setCustomValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');

  // One-time reveal
  const [newlyCreated, setNewlyCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get('/api-keys');
      setKeys(data);
    } catch (e) {
      console.error('List keys failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!name.trim()) {
      setFormError('Give the key a name');
      return;
    }
    if (mode === 'custom' && !customValue.trim()) {
      setFormError('Paste the custom value');
      return;
    }

    setCreating(true);
    try {
      const payload = { name: name.trim(), mode };
      if (mode === 'custom') payload.custom_value = customValue.trim();

      const { data } = await api.post('/api-keys', payload);
      setNewlyCreated(data);
      setKeys((prev) => [data, ...prev]);
      setName('');
      setCustomValue('');
      setMode('generate');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : 'Could not create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      await api.delete(`/api-keys/${keyId}`);
      setKeys((prev) => prev.filter((k) => k.id !== keyId));
      toast.success('Key revoked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke key');
    }
  };

  const copyPlaintext = () => {
    if (!newlyCreated?.plaintext) return;
    navigator.clipboard.writeText(newlyCreated.plaintext);
    setCopied(true);
    toast.success('Key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const dismissReveal = () => {
    setNewlyCreated(null);
    setCopied(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="api-keys-page">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="apikeys-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <Key className="w-5 h-5 text-blue-300" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">API Keys</h1>
          </div>
          <p className="text-slate-400 text-sm max-w-2xl">
            Bearer tokens for scripts, Postman, and partner integrations. Each key
            authenticates as you with your full permissions. Keep them secret.
          </p>
        </div>

        {/* ---------- Create form ---------- */}
        <form
          onSubmit={handleCreate}
          data-testid="create-key-form"
          className="stat-card mb-8 space-y-4"
        >
          <h2 className="text-lg font-semibold">Create a new key</h2>

          <div>
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              data-testid="key-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Daily export job"
              maxLength={80}
            />
            <p className="text-xs text-slate-500 mt-1">A short label so you know what this key is for.</p>
          </div>

          <div>
            <Label className="block mb-2">How should we create it?</Label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 hover:border-slate-700 cursor-pointer bg-slate-950">
                <input
                  type="radio"
                  name="mode"
                  value="generate"
                  checked={mode === 'generate'}
                  onChange={() => setMode('generate')}
                  data-testid="mode-generate"
                  className="mt-1 accent-blue-500"
                />
                <div>
                  <div className="font-medium text-sm">Generate for me</div>
                  <div className="text-xs text-slate-400">
                    We'll produce a secure <code className="text-blue-300">cc_live_</code> key with 24 random characters.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 hover:border-slate-700 cursor-pointer bg-slate-950">
                <input
                  type="radio"
                  name="mode"
                  value="custom"
                  checked={mode === 'custom'}
                  onChange={() => setMode('custom')}
                  data-testid="mode-custom"
                  className="mt-1 accent-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Use a custom value</div>
                  <div className="text-xs text-slate-400 mb-2">
                    Starts with <code className="text-blue-300">cc_live_</code> or <code className="text-blue-300">cc_test_</code>.
                    16–48 chars of letters, digits, hyphens, or underscores after the prefix.
                  </div>
                  {mode === 'custom' && (
                    <Input
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      placeholder="cc_live_YourCustomKeyValueHere"
                      className="font-mono text-sm"
                      data-testid="custom-value-input"
                      autoFocus
                    />
                  )}
                </div>
              </label>
            </div>
          </div>

          {formError && (
            <div
              data-testid="create-error"
              className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={creating}
              className="gradient-accent"
              data-testid="create-key-submit"
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> :
                <><Plus className="w-4 h-4 mr-2" /> Create key</>}
            </Button>
          </div>
        </form>

        {/* ---------- Existing keys ---------- */}
        <div className="stat-card" data-testid="keys-list-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Your keys</h2>
            <span className="text-xs text-slate-500 font-mono" data-testid="keys-count">
              {keys.length} active
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="keys-empty-state">
              No keys yet. Create one above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Preview</th>
                    <th className="px-3 py-2">Created</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr
                      key={k.id}
                      data-testid={`key-row-${k.id}`}
                      className="border-b border-slate-900 hover:bg-slate-900/40"
                    >
                      <td className="px-3 py-3 font-medium">{k.name}</td>
                      <td className="px-3 py-3 font-mono text-xs text-slate-400">
                        {k.prefix}{k.preview}
                      </td>
                      <td className="px-3 py-3 text-slate-400">{formatDate(k.created_at)}</td>
                      <td className="px-3 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevoke(k.id)}
                          data-testid={`revoke-btn-${k.id}`}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-slate-500 mt-4 flex items-start gap-2">
            <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Keys authenticate as you with your full permissions. Rate limit: 60 requests
              per minute, 10,000 per day per key.
            </span>
          </p>
        </div>

        {/* ---------- One-time reveal overlay ---------- */}
        {newlyCreated && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="reveal-panel"
          >
            <div className="bg-slate-900 border border-emerald-500/40 rounded-xl p-6 max-w-xl w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-300" />
                </div>
                <div>
                  <h3 className="font-semibold">Key created</h3>
                  <p className="text-xs text-slate-400">Save this value now — it will not be shown again.</p>
                </div>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 mb-3">
                <div className="text-xs text-slate-500 mb-1">Plaintext (one-time)</div>
                <code
                  className="font-mono text-xs md:text-sm text-emerald-200 break-all block"
                  data-testid="reveal-plaintext"
                >
                  {newlyCreated.plaintext}
                </code>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-5 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-200">
                  After you dismiss this panel, only the last-4 preview (<span className="font-mono">{newlyCreated.preview}</span>) will be visible.
                  Store this value in a password manager before continuing.
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={copyPlaintext}
                  data-testid="reveal-copy-btn"
                  className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                >
                  {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> :
                    <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                </Button>
                <Button
                  onClick={dismissReveal}
                  data-testid="reveal-dismiss-btn"
                  className="gradient-accent"
                >
                  I've saved it
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
